import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { DICT, LOCALES } from "@/lib/i18n";

const AuthCtx = createContext(null);
const PrefsCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);
  useEffect(() => {
    // Returning from Google OAuth: AuthCallback exchanges the session first
    if (window.location.hash?.includes("session_id=")) return;
    checkAuth();
  }, [checkAuth]);
  useEffect(() => {
    const l = localStorage.getItem("gst_lang") || "fr";
    if (user && user.account_status !== "pending" && user.lang !== l) api.put("/auth/lang", { lang: l }).then(() => setUser((u) => ({ ...u, lang: l }))).catch(() => {});
  }, [user]);
  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    window.caches?.delete("gst-v1");
    setUser(false);
  };
  return <AuthCtx.Provider value={{ user, setUser, checkAuth, logout }}>{children}</AuthCtx.Provider>;
}

export function PrefsProvider({ children }) {
  const [lang, setLangState] = useState(localStorage.getItem("gst_lang") || "fr");
  const [currency, setCurrencyState] = useState(localStorage.getItem("gst_cur") || "EUR");
  const [fx, setFx] = useState(null);
  useEffect(() => {
    api.get("/fx").then((r) => setFx(r.data)).catch(() => {});
  }, []);
  const qc = useQueryClient();
  const setLang = (l) => {
    localStorage.setItem("gst_lang", l); setLangState(l);
    api.put("/auth/lang", { lang: l }).then(() => qc.invalidateQueries()).catch(() => {});
  };
  const setCurrency = (c) => { localStorage.setItem("gst_cur", c); setCurrencyState(c); };
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);
  const t = (k) => (DICT[lang] || DICT.fr)[k] ?? DICT.en[k] ?? k;
  const locale = LOCALES[lang] || "fr-FR";
  const fmt = (amount, cur) => {
    try {
      return new Intl.NumberFormat(locale, { style: "currency", currency: cur, maximumFractionDigits: 2 }).format(amount || 0);
    } catch {
      return `${(amount || 0).toFixed(2)} ${cur}`;
    }
  };
  const convert = (amount, from) => {
    const r = fx?.rates;
    if (!r || from === currency || !r[from] || !r[currency]) return null;
    return (amount / r[from]) * r[currency];
  };
  const money = (amount, from = "EUR") => {
    const c = convert(amount, from);
    return c == null ? fmt(amount, from) : fmt(c, currency);
  };
  const fdate = (d) => (d ? new Date(d).toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" }) : "—");
  const fdatetime = (d) => (d ? new Date(d).toLocaleString(locale, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
  return (
    <PrefsCtx.Provider value={{ lang, setLang, currency, setCurrency, fx, t, fmt, money, convert, fdate, fdatetime }}>
      {children}
    </PrefsCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
export const usePrefs = () => useContext(PrefsCtx);
